import React, { useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import useDialog from "../../contexts/DialogProvider";
import { useUser } from "../../contexts/UserProvider";
import PasswordField from "../../Components/PasswordField";
import { useTranslation } from "react-i18next";

interface ILoginFormProps {
  onSubmit: (isSubmitting: boolean) => void;
}

const LoginForm = (props: ILoginFormProps) => {
  const { onSubmit } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const showDialog = useDialog();
  const { login } = useUser();
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: Yup.object({
      username: Yup.string().required(t("Required")),
      password: Yup.string().required(t("Required")),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      onSubmit(true);
      const result = await login(values.username, values.password);
      setIsSubmitting(false);
      onSubmit(false);
      if (result === "fail") {
        showDialog({
          title: t("Authentication failed"),
          text: t("Please, try again."),
        });
        resetForm();
      } else if (result === "ok") {
        let next = "/";
        if (location.state && location.state.next) {
          next = location.state.next;
        }
        navigate(next);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <TextField
        fullWidth
        autoFocus
        margin="normal"
        id="username"
        label={t("Username")}
        type="text"
        disabled={isSubmitting}
        error={formik.touched.username && Boolean(formik.errors.username)}
        helperText={formik.touched.username && formik.errors.username}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.username}
        data-cy="username-field"
      ></TextField>
      <PasswordField
        fullWidth
        margin="normal"
        id="password"
        label={t("Password")}
        disabled={isSubmitting}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.password}
        data-cy="password-field"
      ></PasswordField>
      <Box mt={2} mb={2} display="flex" flexDirection="column">
        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={isSubmitting}
          data-cy="sign-in-button"
        >
          {t("Sign in")}
        </Button>
      </Box>
    </form>
  );
};

export default LoginForm;
